import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, Circle, Plus, X, XCircle } from "lucide-react";
import CampaignExpectation from "components/experiment/designer/CampaignExpectation";
import ConditionMap from "components/experiment/designer/ConditionMap";
import PlanPreviewChart from "components/experiment/designer/PlanPreviewChart";
import {
  createCalibrationPreset,
  estimatePlan,
  getCampaignStatus,
  startCampaign,
  updateCalibrationPreset,
} from "services/experimentApi";
import { HARSHNESS_STYLE, conditionError, harshness, normalizeConditions, parseLevels } from "utils/calibrationCampaign";
import { calibrationPlanSeries, formatDuration } from "utils/planPreview";

const toRow = (c) => ({ label: c.label || "", temp_c: String(c.temp_c ?? ""), rh_pct: String(c.rh_pct ?? "") });
const MAX_CONDITIONS_FALLBACK = 12;

// How the water moves. "alternate" (the default): 15 -> 40 cm at one condition, 40 -> 15 cm at the next.
const WATER_MODES = [
  {
    id: "alternate",
    title: "Up at one condition, down at the next",
    text: "15 → 40 cm, then 40 → 15 cm at the next condition. Fastest; filling and draining are both checked.",
    levels: "15, 20, 25, 30, 35, 40",
    hint: "Written bottom to top; every other condition runs them backwards.",
  },
  {
    id: "updown",
    title: "Up and down at every condition",
    text: "15 → 40 → 15 cm at each condition. Twice the water moves.",
    levels: "15, 20, 25, 30, 35, 40, 35, 30, 25, 20, 15",
    hint: "Up then down, at every condition.",
  },
  {
    id: "level_first",
    title: "Keep the water still, change the air",
    text: "The water stays at one level while the chamber tries every condition. Least pumping.",
    levels: "15, 40, 27.5",
    hint: "Include a fill and a drain, e.g. 15, 40, 27.5.",
  },
];

const modeOf = (ladder, defaults) => {
  if (ladder.order === "level_first") return "level_first";
  const alternate = "alternate" in ladder ? ladder.alternate : defaults.alternate ?? true;
  return alternate ? "alternate" : "updown";
};

/**
 * Step 2 and 3 of the designer for a calibration campaign: the list of chamber conditions (the chamber is set to
 * each in turn while the water runs the ladder), a picture of them on temperature x humidity, the ladder,
 * the readiness checks, a time estimate, and Start. Saving as a preset stores the plan for the team.
 *
 * Props: preset (the picked card), rules (GET /experiment/presets/ rules), controllerMac, waterDeviceMac,
 * onSaved() (refresh the saved presets), onArchive(id), onStarted(campaign).
 */
export default function CalibrationPlanEditor({ preset, rules, controllerMac, waterDeviceMac, onSaved, onArchive, onStarted }) {
  const defaults = rules?.ladder || {};
  const [name, setName] = useState("");
  const [rows, setRows] = useState([]);
  const [levelsText, setLevelsText] = useState("");
  const [holdMin, setHoldMin] = useState("3");
  const [waterMode, setWaterMode] = useState("alternate");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const confirmRef = useRef(null);
  // The confirmation is a second step: bring it into view so the first click visibly does something.
  useEffect(() => {
    if (confirming && confirmRef.current?.scrollIntoView) confirmRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [confirming]);

  const presetKey = preset ? `${preset.key}:${preset.nonce ?? ""}` : "";
  useEffect(() => {
    if (!preset) return;
    setName(preset.blank ? "" : preset.name || "");
    setRows((preset.conditions || []).map(toRow));
    const ladder = preset.ladder && preset.ladder.levels_cm ? preset.ladder : defaults;
    setLevelsText((ladder.levels_cm || []).join(", "));
    setHoldMin(String(Math.round(((ladder.hold_s ?? defaults.hold_s ?? 180) / 60) * 10) / 10));
    setWaterMode(modeOf(preset.ladder && preset.ladder.levels_cm ? preset.ladder : {}, defaults));
    setMessage("");
    setConfirming(false);
    // Only when a different preset is picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const chamber = rules?.chamber;
  const rowErrors = useMemo(() => rows.map((row) => conditionError(row, chamber)), [rows, chamber]);
  const parsedLevels = useMemo(
    () => parseLevels(levelsText, { min: defaults.level_min_cm, max: defaults.level_max_cm, maxCount: defaults.levels_max }),
    [levelsText, defaults.level_min_cm, defaults.level_max_cm, defaults.levels_max]
  );
  const conditions = useMemo(() => normalizeConditions(rows), [rows]);
  const ladder = useMemo(
    () =>
      parsedLevels.levels
        ? {
            levels_cm: parsedLevels.levels,
            hold_s: Math.round(Number(holdMin) * 60) || 180,
            order: waterMode === "level_first" ? "level_first" : "condition_first",
            alternate: waterMode === "alternate",
          }
        : null,
    [parsedLevels, holdMin, waterMode]
  );
  const levelFirst = waterMode === "level_first";
  const mode = WATER_MODES.find((m) => m.id === waterMode) || WATER_MODES[0];
  // A saved preset's own stability settings are kept as they are; the page no longer asks about settling.
  const stability = useMemo(() => preset?.stability || {}, [preset]);
  const chooseMode = (id) => {
    // Swap the levels only while they are still the previous choice's suggestion, never the person's own list.
    if (WATER_MODES.some((m) => m.levels === levelsText.trim())) {
      setLevelsText(WATER_MODES.find((m) => m.id === id).levels);
    }
    setWaterMode(id);
  };
  const firstError =
    (!rows.length && "Add at least one condition.") ||
    rowErrors.find(Boolean) ||
    parsedLevels.error ||
    (levelFirst && parsedLevels.levels && parsedLevels.levels.length > 8 && "Keeping the water at each level takes at most 8 levels.") ||
    (!(Number(holdMin) >= 1 && Number(holdMin) <= 60) && "Hold each level 1-60 minutes.") ||
    null;

  // Readiness, and the sensor's reporting interval (which sets how long each level must hold).
  const refreshStatus = useCallback(async () => {
    if (!controllerMac) return;
    try {
      setStatus(await getCampaignStatus(controllerMac, waterDeviceMac));
    } catch (error) {
      setStatus({ error: error.message, checks: [] });
    }
  }, [controllerMac, waterDeviceMac]);
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // The backend's time estimate, a moment after the plan stops changing.
  const planKey = JSON.stringify([conditions, ladder, stability, status?.sensor_interval_s]);
  useEffect(() => {
    if (firstError) {
      setEstimate(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const result = await estimatePlan({ conditions, ladder, stability, sensorIntervalS: status?.sensor_interval_s });
        if (!cancelled) setEstimate(result.estimate);
      } catch {
        if (!cancelled) setEstimate(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // planKey stands for the plan's contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, firstError]);

  const points = useMemo(() => calibrationPlanSeries(conditions, ladder || {}, estimate), [conditions, ladder, estimate]);
  const checks = status?.checks || [];
  const ready = checks.length > 0 && checks.every((c) => c.ok);
  const maxConditions = rules?.conditions_max ?? MAX_CONDITIONS_FALLBACK;

  const update = (index, patch) => setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const move = (index, delta) =>
    setRows((current) => {
      const next = [...current];
      const [row] = next.splice(index, 1);
      next.splice(index + delta, 0, row);
      return next;
    });
  const remove = (index) => setRows((current) => current.filter((_, i) => i !== index));
  const add = () => setRows((current) => (current.length >= maxConditions ? current : [...current, { label: "", temp_c: "30", rh_pct: "60" }]));

  const save = async (asNew) => {
    if (!name.trim()) {
      setMessage("Name the plan before saving it.");
      return;
    }
    if (firstError) {
      setMessage(firstError);
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const body = { name: name.trim(), description: notes, conditions, ladder, stability };
      if (!asNew && preset?.profileId) await updateCalibrationPreset(preset.profileId, body);
      else await createCalibrationPreset(body);
      setMessage(asNew || !preset?.profileId ? "Saved to the team's presets." : "Preset updated.");
      onSaved?.();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await startCampaign(controllerMac, {
        deviceMac: waterDeviceMac,
        name: name.trim() || preset?.name || "Calibration",
        notes,
        presetKey: preset && !preset.saved && !preset.blank ? preset.key : "",
        profileId: preset?.profileId || null,
        conditions,
        ladder,
        stability,
      });
      setConfirming(false);
      onStarted?.(result.campaign);
    } catch (error) {
      const problems = error.data?.problems;
      setMessage(problems?.length ? `Not started: ${problems.join("; ")}` : error.message);
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="calibration-plan-editor">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_17rem]">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-slate-700">
            Plan name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Radar node 1, harsh vs mild"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </label>
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Conditions, in order ({rows.length}/{maxConditions})
          </span>
          {rows.map((row, index) => {
            const level = harshness({ temp_c: Number(row.temp_c), rh_pct: Number(row.rh_pct) });
            return (
              <div key={index} className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-slate-50 p-2" data-testid="condition-row">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-6 text-center text-sm font-bold text-slate-500">{index + 1}</span>
                  <input
                    aria-label={`Condition ${index + 1} temperature`}
                    type="number"
                    step="0.5"
                    value={row.temp_c}
                    onChange={(e) => update(index, { temp_c: e.target.value })}
                    className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-slate-500">°C</span>
                  <input
                    aria-label={`Condition ${index + 1} humidity`}
                    type="number"
                    step="0.5"
                    value={row.rh_pct}
                    onChange={(e) => update(index, { rh_pct: e.target.value })}
                    className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-slate-500">%RH</span>
                  <input
                    aria-label={`Condition ${index + 1} label`}
                    value={row.label}
                    onChange={(e) => update(index, { label: e.target.value })}
                    placeholder="label (optional)"
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm"
                  />
                  {level && <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${HARSHNESS_STYLE[level]}`}>{level}</span>}

                  <div className="ml-auto flex gap-1">
                    <button type="button" aria-label={`Move condition ${index + 1} up`} disabled={index === 0} onClick={() => move(index, -1)} className="rounded-lg p-1 text-slate-500 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" aria-label={`Move condition ${index + 1} down`} disabled={index === rows.length - 1} onClick={() => move(index, 1)} className="rounded-lg p-1 text-slate-500 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" aria-label={`Remove condition ${index + 1}`} onClick={() => remove(index)} className="rounded-lg p-1 text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                </div>
                {rowErrors[index] && <p className="pl-8 text-xs text-red-700">{rowErrors[index]}</p>}
              </div>
            );
          })}
          <button
            type="button"
            onClick={add}
            disabled={rows.length >= maxConditions}
            className="flex items-center gap-1 self-start rounded-xl border border-dashed border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 disabled:opacity-40"
          >
            <Plus className="h-4 w-4" /> Add condition
          </button>
        </div>
        <div className="flex flex-col items-center gap-1">
          <ConditionMap conditions={rows} chamber={chamber} />
          <p className="text-center text-[11px] text-slate-500">
            Blue box: what the chamber can reach ({chamber ? `${chamber.temp_min_c}-${chamber.temp_max_c} °C, ${chamber.rh_min_pct}-${chamber.rh_max_pct} %RH` : "…"}).
            Include both harsh and mild conditions.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">The water</span>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3" role="radiogroup" aria-label="How the water moves">
          {WATER_MODES.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={waterMode === option.id}
              onClick={() => chooseMode(option.id)}
              className={`rounded-xl border p-2.5 text-left ${waterMode === option.id ? "border-blue-600 bg-white ring-2 ring-blue-200" : "border-slate-200 bg-white"}`}
            >
              <span className="block text-sm font-semibold text-slate-800">{option.title}</span>
              <span className="mt-0.5 block text-xs text-slate-500">{option.text}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_10rem]">
          <label className="text-sm font-semibold text-slate-700">
            Water levels, in order (cm on the ruler)
            <input
              aria-label="Ladder levels"
              value={levelsText}
              onChange={(e) => setLevelsText(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-mono text-sm"
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Wait at each level (min)
            <input
              aria-label="Hold minutes"
              type="number"
              min="1"
              max="60"
              step="0.5"
              value={holdMin}
              onChange={(e) => setHoldMin(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </label>
        </div>
        {parsedLevels.error ? (
          <p className="mt-1 text-xs text-red-700">{parsedLevels.error}</p>
        ) : (
          <p className="mt-1 text-xs text-slate-500">
            {mode.hint} The water sits still this long at each level before its level is read.
          </p>
        )}
        <label className="mt-3 block text-sm font-semibold text-slate-700">
          Notes (optional)
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
        </label>
      </div>

      <PlanPreviewChart points={points} title="What the campaign will do" />

      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[2fr_3fr]">
        <div className="rounded-xl border border-slate-200 p-3" data-testid="campaign-readiness">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Ready to start?</span>
            <button type="button" onClick={refreshStatus} className="text-xs font-semibold text-blue-700">Check again</button>
          </div>
          {status?.error && <p className="mt-1 text-xs text-red-700">{status.error}</p>}
          <ul className="mt-2 flex flex-col gap-1">
            {checks.map((c) => (
              <li key={c.key} className="flex items-center gap-2 text-sm">
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
                <span className={c.ok ? "text-slate-700" : "text-red-700"}>{c.label}</span>
                {!c.ok && c.key === "pipe_opening_set" && (
                  <a href="/config" className="text-xs font-semibold text-blue-700 underline">Set it in Configuration</a>
                )}
              </li>
            ))}
            {!checks.length && !status?.error && (
              <li className="flex items-center gap-2 text-sm text-slate-400"><Circle className="h-4 w-4" /> Checking…</li>
            )}
          </ul>
          {(status?.advisories || []).map((a) => (
            // Worth knowing, never a reason to refuse: Start stays available.
            <p key={a.key} className="mt-2 flex gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-900" data-testid="campaign-advisory">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {a.label}
            </p>
          ))}
          {status?.series && (
            <p className="mt-2 text-xs text-slate-500">
              {status.series === "est"
                ? "The sensor is sending its estimated level (d1_est); the what-if calibrates it by default"
                : "The sensor is not sending its estimated level right now; the what-if calibrates whichever level has enough pairs"}
              {status.sensor_interval_s ? `. It reports every ${Math.round(status.sensor_interval_s)} s` : ""}.
            </p>
          )}
        </div>
        <CampaignExpectation
          estimate={estimate}
          conditionsCount={conditions.length}
          levelFirst={levelFirst}
          sensorIntervalS={status?.sensor_interval_s}
          pumpMinutesPerDay={rules?.pump_minutes_per_day ?? 450}
          firstError={firstError}
        />
      </div>

      {confirming ? (
        <div ref={confirmRef} className="rounded-xl border-2 border-blue-600 bg-blue-50 p-4 shadow-md" role="dialog" aria-label="Confirm campaign">
          <p className="mb-1 text-sm font-bold text-blue-900">One more step: check the plan, then start</p>
          <p className="text-sm text-amber-900">
            This will set the chamber to <strong>{conditions.length}</strong> condition{conditions.length === 1 ? "" : "s"} in turn and move
            the water between <strong>{Math.min(...(ladder?.levels_cm || [0]))}</strong> and <strong>{Math.max(...(ladder?.levels_cm || [0]))} cm</strong> automatically,
            for about {estimate ? formatDuration(estimate.seconds) : "several hours"}. Nothing else can use the chamber meanwhile.
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={start} disabled={busy} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
              {busy ? "Starting…" : "Yes, start the campaign"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => (firstError ? setMessage(firstError) : setConfirming(true))}
            disabled={busy || !ready || !waterDeviceMac}
            className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
          >
            Review and start
          </button>
          <button type="button" onClick={() => save(true)} disabled={busy} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
            Save as my preset
          </button>
          {preset?.profileId && (
            <>
              <button type="button" onClick={() => save(false)} disabled={busy} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
                Update preset
              </button>
              <button type="button" onClick={() => onArchive?.(preset.profileId)} disabled={busy} className="rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-700">
                Delete preset
              </button>
            </>
          )}
        </div>
      )}
      {message && <p role="status" className="text-sm text-slate-700">{message}</p>}
    </div>
  );
}
